/**
 * SeasonsPage — manage named schedule seasons and their player rosters.
 *
 * Route: /team/:teamId/seasons
 *
 * Coaches can:
 *  - Create named seasons  ("Summer 2025 Sunday Night Hockey")
 *  - Link any team player (real or pending invite) to a season
 *  - Import the entire team roster into a season in one tap
 *  - Remove players from a season roster
 *  - Filter the Schedule page by season
 */
import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useOutletContext, useNavigate } from 'react-router-dom';
import {
  ChevronRight, Plus, Trash2, Users, Calendar,
  X, ArrowLeft, Edit2, UserPlus, Download,
  Archive, RotateCcw, Check,
} from 'lucide-react';
import api from '../services/api.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
import toast from 'react-hot-toast';

export default function SeasonsPage() {
  const { teamId } = useParams();
  const { currentSeasonId = null, setCurrentSeason } = useOutletContext() || {}; // v2.5.189
  const navigate = useNavigate();
  // 2026-07-10 (batch-B A2) — was navigate(`/team/${teamId}/schedule`),
  // a route that DOESN'T EXIST (the schedule page is registered as
  // `games` in main.jsx) → 404. Every other link in this file already
  // used /games.
  const goToSeason = (season) => { setCurrentSeason?.(season.id, season.name); toast.success(`Now working in ${season.name}`); navigate(`/team/${teamId}/games?seasonId=${season.id}`); };
  const { isCoach, loading: roleLoading } = useTeamRole(teamId);

  const [seasons,       setSeasons]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [activeSeason,  setActiveSeason]  = useState(null); // season being managed
  const [members,       setMembers]       = useState([]);   // season roster
  const [teamPlayers,   setTeamPlayers]   = useState([]);   // all team members + invites
  const [membersLoading, setMembersLoading] = useState(false);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newName,    setNewName]    = useState('');
  const [newDesc,    setNewDesc]    = useState('');
  // Apr 2026 (Tom_Notes D2/D3) — newMaxPlayers/newAllowOverflow
  // state removed; max-players + allow-overflow live in team
  // settings now. Same for editMaxPlayers/editAllowOverflow below.
  const [creating,   setCreating]   = useState(false);

  // Edit season name inline
  const [editingId,  setEditingId]  = useState(null);
  const [editName,   setEditName]   = useState('');
  const [editDesc,   setEditDesc]   = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [deletingId,   setDeletingId]   = useState(null); // seasonId pending confirm
  const [deleteWorking, setDeleteWorking] = useState(false);
  const [importing,  setImporting]  = useState(false);
  const [showCopyRoster, setShowCopyRoster] = useState(false);
  const [copyFromId,     setCopyFromId]     = useState('');
  const [copying,        setCopying]        = useState(false);
  // Apr 2026 (Tom_Notes D1) — "Based on previous season" picker for
  // the New Season form. When set, after the new season is created
  // we copy the prior season's roster and any per-season cap into
  // the new one. Empty string means "start fresh — just import the
  // current team roster" (the D4 default).
  // Apr 30 2026 (App_Change_List #11, #34) — supplanted by the
  // wizard below. `newBasedOnId` is kept in state only for the
  // legacy "copy roster from another season" UX in the season-
  // detail panel; the create form now uses bringOverPlayers +
  // makeActive instead of auto-importing the team roster.
  const [newBasedOnId,   setNewBasedOnId]   = useState('');
  // Apr 30 2026 (App_Change_List #11) — bringOverPlayers wizard.
  // Default 'none' per item #34 ("do not require importing players").
  // The four options match the backend's VALID_IMPORT enum.
  const [newBringOver,   setNewBringOver]   = useState('none');
  // May 4 2026 — for 'pick' mode: arrays of selected user/invite IDs +
  // the loaded list of active-season members to choose from. Loaded
  // lazily (only when coach picks 'pick') so the page doesn't pay the
  // GET cost on every visit.
  const [pickedUserIds,    setPickedUserIds]    = useState([]);
  const [pickedInviteIds,  setPickedInviteIds]  = useState([]);
  const [pickableMembers,  setPickableMembers]  = useState(null); // null = not loaded yet
  const [pickableLoading,  setPickableLoading]  = useState(false);
  // Apr 30 2026 (App_Change_List #12) — bringOverSettings wizard.
  // Three options matching the backend's VALID_SETTINGS_IMPORT enum.
  // Default 'team' so a coach who taps through the wizard without
  // touching this gets the most coach-friendly behaviour (new season
  // inherits current team config; no need to re-set 60 toggles).
  // 'fromSeason' requires picking a source from the past-seasons list.
  // 'none' means start with empty settings (all defaults).
  const [newBringSettings,     setNewBringSettings]     = useState('team');
  const [newSettingsSourceId,  setNewSettingsSourceId]  = useState('');
  // 2026-07-10 (Phase 2.5) — 'fromTeam' mode: copy settings from ANOTHER
  // team (+ optionally one of ITS seasons). The teams list and the source
  // team's seasons are fetched lazily — only when the coach actually
  // picks this mode — so the page pays no extra GETs in the common case.
  const [myTeams,              setMyTeams]              = useState(null); // null = not loaded
  const [fromTeamId,           setFromTeamId]           = useState('');
  const [fromTeamSeasons,      setFromTeamSeasons]      = useState(null); // null = not loaded
  const [fromTeamSeasonId,     setFromTeamSeasonId]     = useState('');   // '' = the team's current settings

  const loadMyTeams = async () => {
    if (myTeams !== null) return;
    try {
      const { data } = await api.get('/teams');
      setMyTeams(Array.isArray(data) ? data : (data?.teams || []));
    } catch { setMyTeams([]); }
  };
  const loadFromTeamSeasons = async (id) => {
    setFromTeamSeasons(null);
    if (!id) return;
    try {
      const { data } = await api.get(`/teams/${id}/seasons`);
      setFromTeamSeasons(Array.isArray(data) ? data : (data?.seasons || []));
    } catch { setFromTeamSeasons([]); }
  };

  // v2.5.77 (May 7 2026) — player picker search + per-key settings selection.
  //
  // playerSearchQuery filters the bucketed checklist in `pick` mode
  // by case-insensitive name match. Empty string = show all.
  //
  // settingsCustomize toggles a per-key picker below the settings
  // radio. When false, all keys from the selected source are copied
  // (back-compat behaviour). When true, the coach checks specific
  // groups/keys and only those are sent in `pickedSettingKeys`.
  //
  // pickedSettingKeys is a Set<string> of selected top-level setting
  // keys. Initialized to "all known keys" the first time the picker
  // opens, so flipping the switch preserves the current behaviour
  // and the coach only needs to deselect what they DON'T want.
  const [playerSearchQuery,  setPlayerSearchQuery]  = useState('');
  const [settingsCustomize,  setSettingsCustomize]  = useState(false);
  const [pickedSettingKeys,  setPickedSettingKeys]  = useState(() => new Set());

  // SETTINGS_GROUPS (v2.5.77) — categorization of teams.settings JSONB
  // keys for the per-key picker. Keys here mirror what scheduler.js,
  // games.js, and TeamPage.jsx actually read/write.
  //
  // Adding a new setting? Drop it into the right group below — the
  // picker auto-includes it in "Select all" and the "all keys" union.
  // Missing keys (settings not listed here) are NOT picker-visible
  // but ARE still copied when settingsCustomize=false (the default
  // path doesn't go through this list at all).
  //
  // The "?" tooltip in the UI contains the user-facing label; the
  // string IS the JSONB key, so it must match exactly.
  const SETTINGS_GROUPS = useMemo(() => ([
    {
      id: 'pricing',
      emoji: '💰',
      label: 'Pricing & Payments',
      keys: [
        ['payment_enabled',                 'Payments enabled (Stripe)'],
        ['payment_currency',                'Payment currency'],
        ['spare_fee_cents',                 'Spare fee amount'],
        ['spare_pay_required',              'Require payment for spares'],
        ['spare_pay_mode',                  'When spares pay (promote vs upfront)'],
        ['spare_pass_platform_fee',         'Pass platform fee to spare'],
        ['spare_round_up_quarters',         'Round fee to nearest quarter'],
        ['spare_payment_mode',              'Tracking mode (legacy)'],
        ['spare_fee_show_in_notifications', 'Show spare fee in invites'],
        ['spare_goalies_free',              'Spare goalies play free'],
      ],
    },
    {
      id: 'spares',
      emoji: '⚖️',
      label: 'Spare Management',
      keys: [
        ['spare_request_days_before',          'Auto-request days before game'],
        ['spare_auto_invite_include_goalies',  'Include goalies in auto-invite'],
        ['spare_invite_cascade_enabled',       'Cascade spare invites'],
        ['spare_invite_cascade_hours',         'Cascade interval (hours)'],
        ['spare_invite_lists',                 'Spare invite list config'],
        ['waitlist_allow_spares_before_invite','Allow spares to join waitlist early'],
      ],
    },
    {
      id: 'goalies',
      emoji: '🥅',
      label: 'Goalie Management',
      keys: [
        ['goalie_auto_invite_enabled',                'Auto-invite goalies'],
        ['goalie_auto_invite_on_checkout_enabled',    'Auto-invite when goalie checks out'],
        ['goalie_auto_invite_on_checkout_delay_hours','Checkout-invite delay (hours)'],
        ['goalie_invite_cascade_enabled',             'Cascade goalie invites'],
        ['goalie_invite_cascade_hours',               'Goalie cascade interval'],
        ['goalie_invite_lists',                       'Goalie invite list config'],
      ],
    },
    {
      id: 'reminders',
      emoji: '⏰',
      label: 'Reminders & Automation',
      keys: [
        ['auto_balance_enabled',           'Auto-balance teams'],
        ['auto_balance_hours_before',      'Auto-balance lead time'],
        ['auto_mark_ft_in_on_reminder',    'Auto-RSVP full-timers on reminder'],
        ['checkin_lookahead_enabled',      'Check-in look-ahead'],
        ['checkin_lookahead_games',        'Look-ahead game count'],
        ['min_players_to_play',            'Minimum players to play'],
      ],
    },
    {
      id: 'notifications',
      emoji: '💬',
      label: 'Chat & Notifications',
      keys: [
        ['chat_notify_mode',                          'Chat email mode'],
        ['chat_email_all_messages',                   'Email every chat message (legacy)'],
        ['chat_attachment_retention_days',            'Chat attachment retention'],
        ['notify_coach_on_ft_player_checkout',        'Coach alert: FT player out'],
        ['notify_coach_on_ft_goalie_checkout',        'Coach alert: FT goalie out'],
        ['notify_coach_on_spare_player_checkout',     'Coach alert: spare player out'],
        ['notify_coach_on_spare_goalie_checkout',     'Coach alert: spare goalie out'],
        ['notify_coach_checkout_window_hours_ft_player',    'Coach alert timing: FT player'],
        ['notify_coach_checkout_window_hours_ft_goalie',    'Coach alert timing: FT goalie'],
        ['notify_coach_checkout_window_hours_spare_player', 'Coach alert timing: spare player'],
        ['notify_coach_checkout_window_hours_spare_goalie', 'Coach alert timing: spare goalie'],
      ],
    },
    {
      id: 'game',
      emoji: '🎯',
      label: 'Game Settings',
      keys: [
        ['balance_include_goalies',  'Include goalies in balancing'],
        ['skaters_confirmed',        'Skaters confirmed flag'],
        ['goalies_confirmed',        'Goalies confirmed flag'],
        ['drinks_label',             'Drinks tab label'],
      ],
    },
  ]), []);

  // Flat list of every setting key the picker knows about. Used to:
  //   (a) initialize pickedSettingKeys to "everything" when the
  //       customize panel first opens, so toggling the switch is
  //       a no-op in terms of what gets copied
  //   (b) compare `pickedSettingKeys.size === ALL_KEYS.size` →
  //       send pickedSettingKeys=null (= copy all, default behaviour)
  //       so we don't bloat the request when the coach didn't actually
  //       customize anything.
  const ALL_SETTING_KEYS = useMemo(() => {
    const all = [];
    for (const group of SETTINGS_GROUPS) {
      for (const [key] of group.keys) all.push(key);
    }
    return all;
  }, [SETTINGS_GROUPS]);
  // Apr 30 2026 (App_Change_List #10) — makeActive flag. Default
  // true since coaches creating a new season usually want it
  // active immediately. Deactivates the previously-active season
  // (chat + stats scope changes accordingly).
  const [newMakeActive,  setNewMakeActive]  = useState(true);
  const [addingId,   setAddingId]   = useState(null); // userId or inviteId being added
  const [removingId, setRemovingId] = useState(null);
  // Bulk-add: track which rows are currently selected, filter chip, and
  // whether a bulk add is in progress. Keys are `userId` for real members
  // and `inviteId` for pending invites — consistent with `notInSeason` keys.
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [bulkFilter,   setBulkFilter]   = useState('all'); // all | ft | spare | goalie
  const [bulkAdding,   setBulkAdding]   = useState(false);

  // Clear bulk-select state whenever the coach navigates into or out of a
  // season — otherwise selections leak across seasons and a bulk-add from
  // season A could accidentally add everyone to season B.
  useEffect(() => {
    setSelectedKeys(new Set());
    setBulkFilter('all');
  }, [activeSeason?.id]);

  // May 4 2026 — lazy-load the active season's members the first
  // time the coach picks 'pick' mode in the new-season modal. Reset
  // selection when modal closes or mode changes away from pick.
  useEffect(() => {
    if (!showCreate || newBringOver !== 'pick') return;
    if (pickableMembers !== null) return; // already loaded for this open
    const active = seasons.find(s => s.isActive);
    if (!active) {
      setPickableMembers([]);
      return;
    }
    setPickableLoading(true);
    api.get(`/teams/${teamId}/seasons/${active.id}/members`)
      .then(r => {
        setPickableMembers(r.data || []);
        // Default to all selected — nine times out of ten the coach
        // wants most of the roster + just a few omissions, so pre-
        // selecting saves clicks. Coach can deselect-all with a chip.
        const allUserIds   = (r.data || []).filter(m => !m.isPending && m.userId).map(m => m.userId);
        const allInviteIds = (r.data || []).filter(m =>  m.isPending && m.inviteId).map(m => m.inviteId);
        setPickedUserIds(allUserIds);
        setPickedInviteIds(allInviteIds);
      })
      .catch(() => {
        toast.error('Failed to load roster');
        setPickableMembers([]);
      })
      .finally(() => setPickableLoading(false));
  }, [showCreate, newBringOver, teamId, seasons, pickableMembers]);

  // Reset the pickable list when the modal closes or coach changes
  // away from 'pick' so a re-open re-fetches fresh data.
  useEffect(() => {
    if (!showCreate || newBringOver !== 'pick') {
      setPickableMembers(null);
      setPickedUserIds([]);
      setPickedInviteIds([]);
    }
  }, [showCreate, newBringOver]);

  useEffect(() => { if (!roleLoading) loadSeasons(); }, [teamId, roleLoading]);

  const loadSeasons = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/teams/${teamId}/seasons`);
      setSeasons(data);
    } catch { toast.error('Failed to load seasons'); }
    setLoading(false);
  };

  const loadSeasonMembers = async (season) => {
    setActiveSeason(season);
    setMembersLoading(true);
    try {
      const [membersRes, teamRes] = await Promise.all([
        api.get(`/teams/${teamId}/seasons/${season.id}/members`),
        api.get(`/teams/${teamId}/members`),
      ]);
      setMembers(membersRes.data);
      setTeamPlayers(teamRes.data);
    } catch { toast.error('Failed to load season players'); }
    setMembersLoading(false);
  };

  const createSeason = async () => {
    if (!newName.trim()) return toast.error('Name is required');
    if (newBringSettings === 'fromSeason' && !newSettingsSourceId) {
      return toast.error('Pick a source season for settings import');
    }
    // 2026-07-10 (Phase 2.5) — cross-team mode needs a source team.
    if (newBringSettings === 'fromTeam' && !fromTeamId) {
      return toast.error('Pick a team to copy settings from');
    }
    setCreating(true);
    try {
      // Apr 30 2026 (App_Change_List #11, #12, #34) — wizard send.
      // The backend POST handles makeActive (atomic flip of
      // teams.active_season_id + previous active deactivation),
      // bringOverPlayers (copies the requested slice from the
      // previous active season's roster), AND bringOverSettings
      // (#12: copies settings from team-current, a specific past
      // season, or starts fresh). All in one transaction.
      const { data } = await api.post(`/teams/${teamId}/seasons`, {
        name: newName.trim(),
        description: newDesc.trim() || undefined,
        bringOverPlayers: newBringOver,
        // May 4 2026 — pick mode sends explicit ID arrays. Backend
        // ignores these on other modes.
        ...(newBringOver === 'pick'
          ? { pickedUserIds, pickedInviteIds }
          : {}),
        bringOverSettings: newBringSettings,
        ...(newBringSettings === 'fromSeason'
          ? { fromSettingsSeasonId: newSettingsSourceId }
          : {}),
        // 2026-07-10 (Phase 2.5) — cross-team source. fromSettingsSeasonId
        // is optional here: '' = copy the source TEAM's settings bundle.
        ...(newBringSettings === 'fromTeam'
          ? {
              fromSettingsTeamId: fromTeamId,
              ...(fromTeamSeasonId ? { fromSettingsSeasonId: fromTeamSeasonId } : {}),
            }
          : {}),
        // v2.5.77 — pickedSettingKeys is sent only when the coach
        // has actively customized which keys to copy. Two guards:
        //   (a) settingsCustomize must be on (the coach explicitly
        //       opened the picker), AND
        //   (b) the selection must differ from "everything" — if
        //       they opened the picker but kept all checkboxes,
        //       there's no behavioural change vs not sending the
        //       field, so we don't send it. Keeps payloads minimal
        //       and the audit trail honest.
        // The 'team' or 'fromSeason' check is also implicit — those
        // are the only modes where the backend honors the field.
        ...((settingsCustomize
              && (newBringSettings === 'team' || newBringSettings === 'fromSeason' || newBringSettings === 'fromTeam')
              && pickedSettingKeys.size !== ALL_SETTING_KEYS.length)
          ? { pickedSettingKeys: Array.from(pickedSettingKeys) }
          : {}),
        makeActive: !!newMakeActive,
      });
      const memberCount = data.memberCount ?? data.importedCount ?? 0;
      // If the new season was made active, the previously-active
      // season's isActive must flip to false in our local list.
      // The backend already did the DB-level flip; we mirror it
      // here so the UI reflects the new state without a refetch.
      setSeasons(prev => {
        let next = prev;
        if (data.isActive) {
          next = next.map(s => ({ ...s, isActive: s.id === data.id ? true : false }));
        }
        return [{ ...data, memberCount }, ...next];
      });
      setNewName(''); setNewDesc('');
      setNewBringOver('none');
      setNewBringSettings('team');
      setNewSettingsSourceId('');
      setNewMakeActive(true);
      // v2.5.77 — also reset the search query and per-key picker
      // state so a subsequent "+ New Season" click starts clean.
      setPlayerSearchQuery('');
      setSettingsCustomize(false);
      setPickedSettingKeys(new Set());
      setShowCreate(false);
      // Toast wording reflects what actually happened. Four
      // distinguishable cases: empty/with-players × inactive/active.
      const importLabel = (() => {
        if (newBringOver === 'none' || memberCount === 0) return '';
        if (newBringOver === 'all')      return ` with ${memberCount} player${memberCount !== 1 ? 's' : ''} brought over`;
        if (newBringOver === 'fulltime') return ` with ${memberCount} full-time player${memberCount !== 1 ? 's' : ''}`;
        if (newBringOver === 'spares')   return ` with ${memberCount} spare${memberCount !== 1 ? 's' : ''}`;
        if (newBringOver === 'pick')     return ` with ${memberCount} hand-picked player${memberCount !== 1 ? 's' : ''}`;
        return '';
      })();
      const activeLabel = data.isActive ? ' — now active' : '';
      toast.success(`Season "${data.name}" created${importLabel}${activeLabel}.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create season');
    }
    setCreating(false);
  };

  const saveEdit = async (seasonId) => {
    setEditSaving(true);
    try {
      // Apr 2026 (Tom_Notes D2/D3) — same as createSeason: don't
      // overwrite per-season max/overflow from the edit form. The
      // existing DB values are preserved untouched.
      const { data } = await api.patch(`/teams/${teamId}/seasons/${seasonId}`, {
        name: editName.trim(),
        description: editDesc.trim() || undefined,
      });
      setSeasons(prev => prev.map(s => s.id === seasonId ? {
        ...s, name: data.name, description: data.description,
        // Apr 2026 — pick up isActive too in case it gets toggled
        // server-side via the same PATCH (e.g. an admin reactivating
        // an archived season). Keeps local state in sync without an
        // extra refetch.
        isActive: data.isActive !== undefined ? data.isActive : s.isActive,
      } : s));
      if (activeSeason?.id === seasonId) setActiveSeason(prev => ({ ...prev, name: data.name }));
      setEditingId(null);
      toast.success('Season updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
    setEditSaving(false);
  };

  // Apr 30 2026 (App_Change_List #10) — single-active model. The
  // previous "toggle active/archived" UX is gone: with mutual
  // exclusivity, deactivating a season would leave the team with
  // NO active season, which has unclear semantics for chat/stats
  // scoping. The button now only ACTIVATES — to "deactivate" a
  // season, the coach activates a different one. The team can
  // also delete a season outright if they want it gone.
  // Backend's PATCH endpoint atomically deactivates the previously-
  // active season when this one is activated.
  const [togglingId, setTogglingId] = useState(null);
  const activateSeason = async (season) => {
    if (season.isActive) return; // already active — nothing to do
    setTogglingId(season.id);
    try {
      const { data } = await api.patch(`/teams/${teamId}/seasons/${season.id}`, {
        isActive: true,
      });
      // Mirror the atomic flip locally — only ONE season is active.
      setSeasons(prev => prev.map(s => ({
        ...s,
        isActive: s.id === season.id ? !!data.isActive : false,
      })));
      toast.success(`"${season.name}" is now active`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to activate season');
    }
    setTogglingId(null);
  };

  const deleteSeason = async (season) => {
    if (deletingId !== season.id) { setDeletingId(season.id); return; } // first tap: ask
    setDeleteWorking(true);
    try {
      await api.delete(`/teams/${teamId}/seasons/${season.id}`);
      setSeasons(prev => prev.filter(s => s.id !== season.id));
      if (activeSeason?.id === season.id) setActiveSeason(null);
      toast.success('Season deleted');
    } catch { toast.error('Failed to delete season'); }
    setDeletingId(null);
    setDeleteWorking(false);
  };

  const importTeam = async () => {
    if (!activeSeason) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/seasons/${activeSeason.id}/import-team`);
      toast.success(data.message);
      await loadSeasonMembers(activeSeason);
      setSeasons(prev => prev.map(s => s.id === activeSeason.id
        ? { ...s, memberCount: data.count } : s));
    } catch { toast.error('Failed to import players'); }
    setImporting(false);
  };

  const copyRoster = async () => {
    if (!activeSeason || !copyFromId) return;
    setCopying(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/seasons/${activeSeason.id}/copy-roster`, {
        fromSeasonId: copyFromId,
      });
      toast.success(data.message);
      await loadSeasonMembers(activeSeason);
      setSeasons(prev => prev.map(s => s.id === activeSeason.id
        ? { ...s, memberCount: data.count } : s));
      setShowCopyRoster(false);
      setCopyFromId('');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to copy players'); }
    setCopying(false);
  };

  const addPlayer = async (player) => {
    if (!activeSeason) return;
    const key = player.userId || player.inviteId;
    setAddingId(key);
    try {
      await api.post(`/teams/${teamId}/seasons/${activeSeason.id}/members`, {
        userId:   player.isPending ? undefined : player.userId,
        inviteId: player.isPending ? player.inviteId : undefined,
      });
      await loadSeasonMembers(activeSeason);
      toast.success(`${player.name || player.email || 'Player'} added to season`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add player');
    }
    setAddingId(null);
  };

  // Bulk-add: sends individual requests (backend has no bulk-members endpoint,
  // but the single-member endpoint is idempotent via ON CONFLICT DO NOTHING,
  // so retries and concurrent calls are safe). Reports per-player failures
  // in a single toast at the end rather than one per error.
  const addPlayersBulk = async (players) => {
    if (!activeSeason || players.length === 0) return;
    setBulkAdding(true);
    let okCount = 0;
    const failures = [];
    // Run requests in parallel in small batches so we don't hammer the API
    // with 50+ simultaneous requests on a large roster.
    const BATCH = 8;
    for (let i = 0; i < players.length; i += BATCH) {
      const batch = players.slice(i, i + BATCH);
      const results = await Promise.allSettled(batch.map(p =>
        api.post(`/teams/${teamId}/seasons/${activeSeason.id}/members`, {
          userId:   p.isPending ? undefined : p.userId,
          inviteId: p.isPending ? p.inviteId : undefined,
        })
      ));
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') okCount += 1;
        else failures.push(batch[idx].name || batch[idx].email || 'Unknown');
      });
    }
    await loadSeasonMembers(activeSeason);
    if (failures.length === 0) {
      toast.success(`Added ${okCount} player${okCount === 1 ? '' : 's'} to season`);
    } else if (okCount > 0) {
      toast.error(`Added ${okCount}, but ${failures.length} failed (e.g. ${failures[0]})`, { duration: 5000 });
    } else {
      toast.error(`Failed to add any players (e.g. ${failures[0]})`, { duration: 5000 });
    }
    setSelectedKeys(new Set());
    setBulkAdding(false);
  };

  const removePlayer = async (seasonMemberId, name) => {
    if (!activeSeason) return;
    setRemovingId(seasonMemberId);
    try {
      await api.delete(`/teams/${teamId}/seasons/${activeSeason.id}/members/${seasonMemberId}`);
      setMembers(prev => prev.filter(m => m.seasonMemberId !== seasonMemberId));
      setSeasons(prev => prev.map(s => s.id === activeSeason.id
        ? { ...s, memberCount: Math.max(0, s.memberCount - 1) } : s));
      toast.success(`${name || 'Player'} removed from season`);
    } catch { toast.error('Failed to remove player'); }
    setRemovingId(null);
  };

  // Players not yet in this season's roster
  const memberIds = useMemo(() => new Set([
    ...members.filter(m => m.userId).map(m => m.userId),
    ...members.filter(m => m.inviteId).map(m => m.inviteId),
  ]), [members]);

  const notInSeason = useMemo(() => teamPlayers.filter(p => {
    const key = p.isPending ? p.inviteId : p.userId;
    return key && !memberIds.has(key) && !['coach','manager'].includes(p.role);
  }), [teamPlayers, memberIds]);

  if (loading || roleLoading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  }

  // ── Season roster detail view ───────────────────────────────────────────────
  if (activeSeason) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Back + header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setActiveSeason(null)} className="btn-ghost px-3 py-2">
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="section-title text-2xl truncate">{activeSeason.name}</h1>
            {activeSeason.description && (
              <p className="text-sm text-secondary mt-0.5">{activeSeason.description}</p>
            )}
          </div>
          <Link to={`/team/${teamId}/games?seasonId=${activeSeason.id}`} className="btn-ghost text-sm px-3 py-2 flex items-center gap-2">
            <Calendar size={14} /> View Games
          </Link>
        </div>

        {/* Import all button */}
        <div className="card mb-3 flex items-center gap-4">
          <div className="flex-1">
            <p className="font-semibold text-sm">Import entire team player list</p>
            <p className="text-xs text-secondary mt-0.5">
              Adds all current players and pending invites to this season in one tap.
            </p>
          </div>
          <button onClick={importTeam} disabled={importing}
            className="btn-ice flex-shrink-0 text-sm px-4 py-2 flex items-center gap-2">
            {importing
              ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Importing…</>
              : <><Download size={14} /> Import All</>}
          </button>
        </div>

        {/* Copy roster from another season */}
        {seasons.filter(s => s.id !== activeSeason.id).length > 0 && (
          <div className="card mb-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="font-semibold text-sm">Copy players from a prior season</p>
                <p className="text-xs text-secondary mt-0.5">
                  Carry forward your team from another season — great for recurring leagues.
                </p>
              </div>
              <button
                onClick={() => setShowCopyRoster(v => !v)}
                className="btn-ghost flex-shrink-0 text-sm px-3 py-2 flex items-center gap-2">
                {showCopyRoster ? 'Cancel' : '📋 Copy'}
              </button>
            </div>
            {showCopyRoster && (
              <div className="mt-3 flex gap-2 items-center flex-wrap">
                <select
                  className="input-field flex-1 py-2 text-sm"
                  value={copyFromId}
                  onChange={e => setCopyFromId(e.target.value)}>
                  <option value="">— Choose source season —</option>
                  {seasons.filter(s => s.id !== activeSeason.id).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <button
                  onClick={copyRoster}
                  disabled={copying || !copyFromId}
                  className="btn-ice flex-shrink-0 py-2 px-4 text-sm flex items-center gap-2">
                  {copying
                    ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Copying…</>
                    : 'Copy Players →'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Season roster */}
        <div className="card mb-4">
          <h2 className="font-bold mb-3 flex items-center gap-2">
            <Users size={15} style={{ color: 'var(--ice)' }} />
            Season Players
            <span className="text-xs font-normal text-secondary ml-1">({members.length} players)</span>
          </h2>
          {membersLoading ? (
            <div className="py-6 text-center"><div className="spinner mx-auto" /></div>
          ) : members.length === 0 ? (
            <p className="text-sm text-secondary py-4 text-center">
              No players yet. Import your team's player list or add players individually below.
            </p>
          ) : (
            <div className="space-y-1.5">
              {members.map(m => (
                <div key={m.seasonMemberId} className="flex items-center gap-3 py-2 px-3 rounded-xl"
                  style={{ background: 'var(--surface-2)' }}>
                  {/* Avatar */}
                  <div style={{
                    width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                    background: m.isPending ? 'rgba(255,149,0,0.15)' : 'var(--ice-a12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                    color: m.isPending ? 'var(--puck)' : 'var(--ice)',
                  }}>
                    {(m.name || m.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{m.name || m.email || 'Unknown'}</p>
                    {m.email && m.name && (
                      <p className="text-xs text-secondary truncate">{m.email}</p>
                    )}
                    {m.isPending && (
                      <span className="text-xs" style={{ color: 'var(--puck)' }}>
                        {m.inviteSent ? '✉ Invited' : '📋 Not invited yet'}
                      </span>
                    )}
                  </div>
                  {m.rating && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-md"
                      style={{ background: 'var(--surface-3)', color: 'var(--text-2)' }}>
                      R{m.rating}
                    </span>
                  )}
                  <button
                    onClick={() => removePlayer(m.seasonMemberId, m.name || m.email)}
                    disabled={removingId === m.seasonMemberId}
                    className="btn-ghost p-1.5 flex-shrink-0"
                    style={{ color: 'var(--penalty)' }}
                    title="Remove from season">
                    {removingId === m.seasonMemberId
                      ? <div className="spinner" style={{ width: 13, height: 13 }} />
                      : <X size={14} />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add players — with bulk-select */}
        {notInSeason.length > 0 && (() => {
          // Filter the pool by the active chip. FT = full-time non-goalie,
          // Spare = spare non-goalie, Goalie = any goalie regardless of primary/spare.
          const filtered = notInSeason.filter(p => {
            if (bulkFilter === 'all') return true;
            const isGoalie = p.position === 'goalie';
            if (bulkFilter === 'goalie') return isGoalie;
            if (bulkFilter === 'ft')     return !isGoalie && p.isPrimary;
            if (bulkFilter === 'spare')  return !isGoalie && !p.isPrimary;
            return true;
          });
          const filteredKeys = filtered.map(p => p.isPending ? p.inviteId : p.userId).filter(Boolean);
          const allFilteredSelected = filteredKeys.length > 0 && filteredKeys.every(k => selectedKeys.has(k));

          const toggleKey = (key) => {
            setSelectedKeys(prev => {
              const next = new Set(prev);
              if (next.has(key)) next.delete(key); else next.add(key);
              return next;
            });
          };
          const toggleAllFiltered = () => {
            if (allFilteredSelected) {
              setSelectedKeys(prev => {
                const next = new Set(prev);
                filteredKeys.forEach(k => next.delete(k));
                return next;
              });
            } else {
              setSelectedKeys(prev => {
                const next = new Set(prev);
                filteredKeys.forEach(k => next.add(k));
                return next;
              });
            }
          };
          const clearSelection = () => setSelectedKeys(new Set());
          const onBulkAdd = () => {
            const toAdd = notInSeason.filter(p => {
              const k = p.isPending ? p.inviteId : p.userId;
              return k && selectedKeys.has(k);
            });
            addPlayersBulk(toAdd);
          };

          const FilterChip = ({ value, label, count }) => (
            <button
              onClick={() => setBulkFilter(value)}
              className="text-xs px-3 py-1.5 rounded-full transition-all"
              style={{
                background:    bulkFilter === value ? 'var(--ice-a15)' : 'var(--surface-2)',
                color:         bulkFilter === value ? 'var(--ice)'     : 'var(--text-2)',
                border: `1px solid ${bulkFilter === value ? 'var(--ice-a40)' : 'var(--border)'}`,
                fontWeight:    bulkFilter === value ? 700 : 500,
              }}>
              {label}{count !== undefined && <span className="opacity-60 ml-1">({count})</span>}
            </button>
          );

          const counts = {
            all:    notInSeason.length,
            ft:     notInSeason.filter(p => p.position !== 'goalie' && p.isPrimary).length,
            spare:  notInSeason.filter(p => p.position !== 'goalie' && !p.isPrimary).length,
            goalie: notInSeason.filter(p => p.position === 'goalie').length,
          };

          return (
            <div className="card">
              <h2 className="font-bold mb-3 flex items-center gap-2">
                <UserPlus size={15} style={{ color: 'var(--goal)' }} />
                Add Players
                <span className="text-xs font-normal text-secondary ml-1">
                  ({notInSeason.length} not in season)
                </span>
              </h2>

              {/* Filter chips — let the coach narrow by player type */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                <FilterChip value="all"    label="All"         count={counts.all} />
                <FilterChip value="ft"     label="FT Players"  count={counts.ft} />
                <FilterChip value="spare"  label="Spares"      count={counts.spare} />
                <FilterChip value="goalie" label="Goalies"     count={counts.goalie} />
              </div>

              {/* Select-all affordance for the filtered subset */}
              {filtered.length > 0 && (
                <div className="flex items-center justify-between mb-2 px-1">
                  <button
                    onClick={toggleAllFiltered}
                    className="text-xs font-semibold flex items-center gap-1.5"
                    style={{ color: 'var(--ice)' }}>
                    <span style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      background: allFilteredSelected ? 'var(--ice)' : 'var(--surface-3)',
                      border: `1.5px solid ${allFilteredSelected ? 'var(--ice)' : 'var(--border)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#000', fontSize: 10, fontWeight: 800,
                    }}>
                      {allFilteredSelected ? '✓' : ''}
                    </span>
                    {allFilteredSelected ? 'Deselect all' : 'Select all'} ({filtered.length})
                  </button>
                  {selectedKeys.size > 0 && (
                    <button onClick={clearSelection} className="text-xs text-secondary hover:text-primary">
                      Clear ({selectedKeys.size})
                    </button>
                  )}
                </div>
              )}

              {filtered.length === 0 ? (
                <p className="text-sm text-secondary text-center py-6">
                  No players match this filter.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {filtered.map(p => {
                    const key = p.isPending ? p.inviteId : p.userId;
                    const isSelected = selectedKeys.has(key);
                    return (
                      <div key={key}
                        onClick={() => toggleKey(key)}
                        className="flex items-center gap-3 py-2 px-3 rounded-xl cursor-pointer transition-colors"
                        style={{
                          background: isSelected ? 'var(--ice-a8)' : 'var(--surface-2)',
                          border: `1px solid ${isSelected ? 'var(--ice-a30)' : 'transparent'}`,
                        }}>
                        {/* Checkbox */}
                        <div style={{
                          width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                          background: isSelected ? 'var(--ice)' : 'var(--surface-3)',
                          border: `1.5px solid ${isSelected ? 'var(--ice)' : 'var(--border)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#000', fontSize: 12, fontWeight: 800,
                        }}>
                          {isSelected ? '✓' : ''}
                        </div>

                        <div style={{
                          width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                          background: p.isPending ? 'rgba(255,149,0,0.12)' : 'var(--surface-3)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700, color: 'var(--text-2)',
                        }}>
                          {(p.name || p.email || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{p.name || p.email || 'Unknown'}</p>
                          <div className="flex items-center gap-1.5 text-xs text-secondary">
                            {p.position === 'goalie' && <span>🥅 Goalie</span>}
                            {p.position && p.position !== 'goalie' && <span className="capitalize">{p.position}</span>}
                            {!p.isPrimary && <span style={{ color: 'var(--puck)' }}>• Spare</span>}
                            {p.isPending && <span style={{ color: 'var(--puck)' }}>• Pending invite</span>}
                          </div>
                        </div>
                        {/* Quick single-add button — still available for one-offs */}
                        <button
                          onClick={(e) => { e.stopPropagation(); addPlayer(p); }}
                          disabled={addingId === key || bulkAdding}
                          className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5 flex-shrink-0"
                          style={{ color: 'var(--goal)', borderColor: 'rgba(0,232,122,0.3)' }}>
                          {addingId === key
                            ? <div className="spinner" style={{ width: 12, height: 12 }} />
                            : <><Plus size={12} /> Add</>}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Sticky bulk-add action bar */}
              {selectedKeys.size > 0 && (
                <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between gap-3">
                  <span className="text-sm text-secondary">
                    <strong style={{ color: 'var(--ice)' }}>{selectedKeys.size}</strong> selected
                  </span>
                  <button
                    onClick={onBulkAdd}
                    disabled={bulkAdding}
                    className="btn-ice text-sm px-4 py-2 flex items-center gap-2">
                    {bulkAdding
                      ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Adding…</>
                      : <><Plus size={14} /> Add {selectedKeys.size} to season</>}
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );
  }

  // ── Season list view ────────────────────────────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="section-title text-3xl">Seasons</h1>
          <p className="text-sm text-secondary mt-1">
            Group your schedule into named seasons — each with its own player list.
          </p>
        </div>
        {isCoach && (
          <button onClick={() => setShowCreate(true)} className="btn-ice flex items-center gap-2">
            <Plus size={15} /> New Season
          </button>
        )}
      </div>

      {/* Create season form */}
      {showCreate && (
        <div className="card mb-4">
          <h2 className="font-bold mb-3">New Season</h2>
          <div className="space-y-3">
            <div>
              <label className="label">Season Name *</label>
              <input
                autoFocus type="text" className="input-field"
                placeholder='e.g. "Summer 2025 Sunday Night Hockey"'
                maxLength={120} value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') createSeason(); if (e.key === 'Escape') setShowCreate(false); }}
              />
            </div>
            <div>
              <label className="label">Description <span className="opacity-50 text-xs">(optional)</span></label>
              <input type="text" className="input-field"
                placeholder='e.g. "Ice time at Sportsworld, 21:00 Sundays"'
                maxLength={200} value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
              />
            </div>
            {/* Apr 30 2026 (App_Change_List #11, #34) — bring-over
                wizard. Replaces the legacy "Based on previous season"
                dropdown with explicit radio choices that match the
                backend's bringOverPlayers enum. Default 'none' per
                item #34 ("do not require importing players"). The
                radios are only shown when the team has a current
                active season to bring players from — otherwise the
                roster starts empty regardless. */}
            {seasons.some(s => s.isActive) && (
              <div>
                <label className="label">Bring over players from current season?</label>
                <div className="space-y-1.5 mt-1.5">
                  {[
                    { v: 'none',     l: 'None',                     d: 'Start with an empty roster' },
                    { v: 'all',      l: 'All players',              d: 'Copy every full-time + spare' },
                    { v: 'fulltime', l: 'Full-time players only',   d: 'Skip the spare list' },
                    { v: 'spares',   l: 'Spares only',              d: 'Copy just the spare list' },
                    // May 4 2026 — pick mode lets the coach choose
                    // specific players. Useful for sub-leagues, dropouts,
                    // or starting a season with a deliberate subset.
                    { v: 'pick',     l: 'Pick specific players…',   d: 'Choose individual players to copy over' },
                  ].map(opt => {
                    const active = newBringOver === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setNewBringOver(opt.v)}
                        className="w-full text-left p-3 rounded-lg flex items-start gap-3"
                        style={{
                          background: active ? 'var(--ice-a12)' : 'var(--surface-2)',
                          border: `1.5px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                        }}>
                        <span style={{
                          width: 16, height: 16, borderRadius: '50%',
                          border: `2px solid ${active ? 'var(--ice)' : 'var(--text-3)'}`,
                          flexShrink: 0, marginTop: 2,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {active && <span style={{
                            width: 8, height: 8, borderRadius: '50%', background: 'var(--ice)',
                          }} />}
                        </span>
                        <span className="flex-1">
                          <p className="text-sm font-semibold"
                            style={{ color: active ? 'var(--ice)' : 'var(--text)' }}>
                            {opt.l}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                            {opt.d}
                          </p>
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* May 4 2026 — pick-mode picker. Players grouped by
                    FT players / FT goalies / spare players / spare
                    goalies so the coach can scan + multi-select.
                    Loaded lazily once 'pick' is chosen. ── */}
                {newBringOver === 'pick' && (
                  <div className="mt-3 p-3 rounded-lg" style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--ice-a30)',
                  }}>
                    {pickableLoading && (
                      <div className="text-xs text-secondary py-2 text-center">
                        Loading current season's roster…
                      </div>
                    )}
                    {!pickableLoading && pickableMembers !== null && pickableMembers.length === 0 && (
                      <div className="text-xs text-secondary py-2 text-center">
                        No players in the current active season to pick from.
                      </div>
                    )}
                    {!pickableLoading && pickableMembers !== null && pickableMembers.length > 0 && (() => {
                      // v2.5.77 — search filter. Case-insensitive
                      // match against the player's display name. We
                      // filter pickableMembers BEFORE bucketing so
                      // each tier reflects only matching rows.
                      // An empty query passes everything through.
                      const q = (playerSearchQuery || '').trim().toLowerCase();
                      const visibleMembers = q
                        ? pickableMembers.filter(m =>
                            (m.name || '').toLowerCase().includes(q))
                        : pickableMembers;

                      // Bucket members the same way the GamePage Awaiting
                      // Response section does, so the wording is consistent
                      // across the app.
                      const isGoalieRow = (m) => m.position === 'goalie';
                      const buckets = {
                        ft_players:    visibleMembers.filter(m =>  m.isPrimary && !isGoalieRow(m)),
                        ft_goalies:    visibleMembers.filter(m =>  m.isPrimary &&  isGoalieRow(m)),
                        spare_players: visibleMembers.filter(m => !m.isPrimary && !isGoalieRow(m)),
                        spare_goalies: visibleMembers.filter(m => !m.isPrimary &&  isGoalieRow(m)),
                      };
                      // v2.5.77 — totalPicked counts globally (regardless
                      // of search filter), so the coach sees their full
                      // selection state even when filtering. totalAvailable
                      // also stays at the full roster size; the picker
                      // is still a subset operation across the entire
                      // source season.
                      const totalPicked = pickedUserIds.length + pickedInviteIds.length;
                      const totalAvailable = pickableMembers.length;
                      const togglePick = (m) => {
                        if (m.isPending && m.inviteId) {
                          setPickedInviteIds(prev => prev.includes(m.inviteId)
                            ? prev.filter(id => id !== m.inviteId)
                            : [...prev, m.inviteId]);
                        } else if (!m.isPending && m.userId) {
                          setPickedUserIds(prev => prev.includes(m.userId)
                            ? prev.filter(id => id !== m.userId)
                            : [...prev, m.userId]);
                        }
                      };
                      const isPicked = (m) => m.isPending
                        ? pickedInviteIds.includes(m.inviteId)
                        : pickedUserIds.includes(m.userId);
                      const selectAllInBucket = (bucket) => {
                        const userIds   = bucket.filter(m => !m.isPending && m.userId).map(m => m.userId);
                        const inviteIds = bucket.filter(m =>  m.isPending && m.inviteId).map(m => m.inviteId);
                        setPickedUserIds(prev => [...new Set([...prev, ...userIds])]);
                        setPickedInviteIds(prev => [...new Set([...prev, ...inviteIds])]);
                      };
                      const deselectBucket = (bucket) => {
                        const userIds   = new Set(bucket.filter(m => !m.isPending && m.userId).map(m => m.userId));
                        const inviteIds = new Set(bucket.filter(m =>  m.isPending && m.inviteId).map(m => m.inviteId));
                        setPickedUserIds(prev => prev.filter(id => !userIds.has(id)));
                        setPickedInviteIds(prev => prev.filter(id => !inviteIds.has(id)));
                      };
                      const renderBucket = (label, emoji, bucket) => {
                        if (bucket.length === 0) return null;
                        const allSelected = bucket.every(isPicked);
                        const someSelected = bucket.some(isPicked);
                        return (
                          <div key={label} className="mb-3">
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <p className="text-xs font-bold" style={{
                                color: 'var(--text-2)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                              }}>
                                {emoji} {label} ({bucket.length})
                              </p>
                              <button
                                type="button"
                                onClick={() => allSelected ? deselectBucket(bucket) : selectAllInBucket(bucket)}
                                className="text-xs"
                                style={{
                                  color: 'var(--ice)', fontWeight: 700,
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  padding: 0,
                                }}>
                                {allSelected ? 'Deselect all' : someSelected ? 'Select all' : 'Select all'}
                              </button>
                            </div>
                            <div className="space-y-1">
                              {bucket.map(m => {
                                const picked = isPicked(m);
                                return (
                                  <label
                                    key={m.userId || m.inviteId}
                                    className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer"
                                    style={{
                                      background: picked ? 'rgba(0,212,255,0.10)' : 'transparent',
                                      border: `1px solid ${picked ? 'var(--ice-a30)' : 'transparent'}`,
                                    }}>
                                    <input
                                      type="checkbox"
                                      checked={picked}
                                      onChange={() => togglePick(m)}
                                      style={{ flexShrink: 0 }}
                                    />
                                    <span className="text-sm flex-1 truncate" style={{ color: 'var(--text)' }}>
                                      {m.name || (m.isPending ? '(pending invite)' : 'Unnamed')}
                                    </span>
                                    {m.isPending && (
                                      <span className="text-xs" style={{ color: 'var(--text-3)' }}>📋 pending</span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      };
                      return (
                        <>
                          {/* v2.5.77 — search bar. Filters across all
                              four buckets (FT players, FT goalies,
                              spare players, spare goalies). Sticks
                              above the count chrome so the coach can
                              keep an eye on totals while typing. */}
                          <div className="mb-2">
                            <input
                              type="text"
                              className="input-field"
                              style={{ fontSize: 13, padding: '8px 12px' }}
                              placeholder="Search players by name…"
                              value={playerSearchQuery}
                              onChange={e => setPlayerSearchQuery(e.target.value)}
                              // Stop propagation so typing doesn't
                              // trigger the modal's outer onKeyDown
                              // (Enter would otherwise create the
                              // season mid-search).
                              onKeyDown={e => e.stopPropagation()}
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2 mb-3 pb-2"
                            style={{ borderBottom: '1px solid var(--border)' }}>
                            <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                              {totalPicked} of {totalAvailable} selected
                              {q && (
                                <span className="text-xs ml-2" style={{ color: 'var(--text-2)', fontWeight: 400 }}>
                                  ({visibleMembers.length} match{visibleMembers.length === 1 ? '' : 'es'})
                                </span>
                              )}
                            </p>
                            <div className="flex gap-2">
                              <button type="button"
                                onClick={() => {
                                  setPickedUserIds(pickableMembers.filter(m => !m.isPending && m.userId).map(m => m.userId));
                                  setPickedInviteIds(pickableMembers.filter(m =>  m.isPending && m.inviteId).map(m => m.inviteId));
                                }}
                                className="text-xs font-bold"
                                style={{ color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                Select all
                              </button>
                              <span style={{ color: 'var(--text-3)' }}>·</span>
                              <button type="button"
                                onClick={() => { setPickedUserIds([]); setPickedInviteIds([]); }}
                                className="text-xs font-bold"
                                style={{ color: 'var(--text-2)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                Clear
                              </button>
                            </div>
                          </div>
                          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                            {visibleMembers.length === 0 ? (
                              <div className="text-xs text-secondary py-4 text-center">
                                No players match "{playerSearchQuery}"
                              </div>
                            ) : (
                              <>
                                {renderBucket('Full-time players', '👤', buckets.ft_players)}
                                {renderBucket('Full-time goalies', '🥅', buckets.ft_goalies)}
                                {renderBucket('Spare players', '⏳', buckets.spare_players)}
                                {renderBucket('Spare goalies', '⏳🥅', buckets.spare_goalies)}
                              </>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
            {/* Apr 30 2026 (App_Change_List #12) — bring-over team
                settings. Three radio choices matching the backend's
                VALID_SETTINGS_IMPORT enum. Default 'team' so the
                wizard's tap-through path keeps a coach's existing
                config (60+ toggles in teams.settings) intact in the
                new season. The 'fromSeason' option reveals a season
                picker; 'none' means start with all-defaults. */}
            <div>
              <label className="label">Bring over team settings?</label>
              <div className="space-y-1.5 mt-1.5">
                {[
                  { v: 'team',       l: 'Inherit current settings',     d: 'Copy the active season\'s settings (recommended)' },
                  { v: 'fromSeason', l: 'Copy from another season…',    d: 'Pick a past season to copy settings from' },
                  // 2026-07-10 (Phase 2.5) — cross-team settings source.
                  { v: 'fromTeam',   l: 'Copy from another team…',      d: 'Copy settings from a team you coach or manage (optionally one of its seasons)' },
                  { v: 'none',       l: 'Start fresh with defaults',    d: 'New season has no custom settings' },
                ].map(opt => {
                  const active = newBringSettings === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => {
                        setNewBringSettings(opt.v);
                        // 2.5 — lazy-load the coach's teams for the picker.
                        if (opt.v === 'fromTeam') loadMyTeams();
                      }}
                      className="w-full text-left p-3 rounded-lg flex items-start gap-3"
                      style={{
                        background: active ? 'var(--ice-a12)' : 'var(--surface-2)',
                        border: `1.5px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                      }}>
                      <span style={{
                        width: 16, height: 16, borderRadius: '50%',
                        border: `2px solid ${active ? 'var(--ice)' : 'var(--text-3)'}`,
                        flexShrink: 0, marginTop: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {active && <span style={{
                          width: 8, height: 8, borderRadius: '50%', background: 'var(--ice)',
                        }} />}
                      </span>
                      <span className="flex-1">
                        <p className="text-sm font-semibold"
                          style={{ color: active ? 'var(--ice)' : 'var(--text)' }}>
                          {opt.l}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                          {opt.d}
                        </p>
                      </span>
                    </button>
                  );
                })}
              </div>
              {/* Source-season picker, only when 'fromSeason' is
                  selected. We list every existing season except the
                  one we're about to create (by definition not yet
                  in seasons[]). The dropdown stays empty when the
                  team has no other seasons — the coach would have
                  to pick 'team' or 'none' instead. */}
              {newBringSettings === 'fromSeason' && (
                <div className="mt-2">
                  <select
                    className="input-field"
                    value={newSettingsSourceId}
                    onChange={e => setNewSettingsSourceId(e.target.value)}>
                    <option value="">— pick a season —</option>
                    {seasons.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.isActive ? ' (active)' : ''}
                      </option>
                    ))}
                  </select>
                  {seasons.length === 0 && (
                    <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                      No other seasons to copy from. Pick "Inherit" or "Start fresh."
                    </p>
                  )}
                </div>
              )}

              {/* 2026-07-10 (Phase 2.5) — cross-team pickers. Team list is
                  filtered to coach/manager teams (the backend enforces the
                  same guard); season dropdown is optional — leaving it on
                  "current team settings" copies the source TEAM's bundle. */}
              {newBringSettings === 'fromTeam' && (
                <div className="mt-2 space-y-2">
                  {(() => {
                    const src = (myTeams || []).filter(t =>
                      ['coach', 'manager'].includes(t.member_role) && t.id !== teamId);
                    if (myTeams === null) {
                      return <p className="text-xs" style={{ color: 'var(--text-3)' }}>Loading your teams…</p>;
                    }
                    if (src.length === 0) {
                      return (
                        <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                          You don't coach or manage any other teams. Pick "Inherit" or "Start fresh."
                        </p>
                      );
                    }
                    return (
                      <>
                        <select
                          className="input-field"
                          value={fromTeamId}
                          onChange={e => {
                            setFromTeamId(e.target.value);
                            setFromTeamSeasonId('');
                            loadFromTeamSeasons(e.target.value);
                          }}>
                          <option value="">— pick a team —</option>
                          {src.map(t => (
                            <option key={t.id} value={t.id}>
                              {t.name}{t.league_name ? ` · ${t.league_name}` : ''}
                            </option>
                          ))}
                        </select>
                        {fromTeamId && (
                          <select
                            className="input-field"
                            value={fromTeamSeasonId}
                            onChange={e => setFromTeamSeasonId(e.target.value)}>
                            <option value="">That team's current settings</option>
                            {(fromTeamSeasons || []).map(s => (
                              <option key={s.id} value={s.id}>
                                Season: {s.name}{s.isActive ? ' (active)' : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* v2.5.77 — per-key settings picker. Shows for both
                  'team' and 'fromSeason' modes (the two modes that
                  have a source to filter from). Collapsed by default
                  to keep the modal short for the common case where
                  the coach wants ALL settings copied. ── */}
              {(newBringSettings === 'team' || newBringSettings === 'fromSeason' || newBringSettings === 'fromTeam') && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSettingsCustomize(prev => {
                        const next = !prev;
                        if (next) {
                          // Opening for the first time → seed with
                          // ALL keys checked so flipping the switch
                          // is a no-op until the coach actively
                          // unchecks something. Same model as the
                          // bulk operations in the player picker.
                          setPickedSettingKeys(new Set(ALL_SETTING_KEYS));
                        }
                        return next;
                      });
                    }}
                    className="text-xs font-semibold flex items-center gap-1.5"
                    style={{
                      color: 'var(--ice)',
                      background: 'none', border: 'none',
                      cursor: 'pointer', padding: 0,
                    }}>
                    <span>{settingsCustomize ? '▾' : '▸'}</span>
                    Customize which settings to copy
                    {settingsCustomize && pickedSettingKeys.size !== ALL_SETTING_KEYS.length && (
                      <span style={{
                        marginLeft: 6,
                        padding: '1px 7px',
                        borderRadius: 100,
                        background: 'var(--ice-a15)',
                        color: 'var(--ice)',
                        fontSize: 10,
                        fontWeight: 700,
                      }}>
                        {pickedSettingKeys.size}/{ALL_SETTING_KEYS.length}
                      </span>
                    )}
                  </button>

                  {settingsCustomize && (
                    <div className="mt-2 p-3 rounded-lg" style={{
                      background: 'var(--surface-2)',
                      border: '1px solid var(--ice-a30)',
                    }}>
                      <div className="flex items-center justify-between gap-2 mb-3 pb-2"
                        style={{ borderBottom: '1px solid var(--border)' }}>
                        <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                          Pick which settings groups to copy. Anything you
                          uncheck will fall back to defaults in the new
                          season.
                        </p>
                        <div className="flex gap-2 flex-shrink-0">
                          <button type="button"
                            onClick={() => setPickedSettingKeys(new Set(ALL_SETTING_KEYS))}
                            className="text-xs font-bold"
                            style={{ color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            All
                          </button>
                          <span style={{ color: 'var(--text-3)' }}>·</span>
                          <button type="button"
                            onClick={() => setPickedSettingKeys(new Set())}
                            className="text-xs font-bold"
                            style={{ color: 'var(--text-2)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            None
                          </button>
                        </div>
                      </div>

                      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                        {SETTINGS_GROUPS.map(group => {
                          // For each group, compute selection state:
                          //   • all selected   → checkbox checked
                          //   • some selected  → checkbox indeterminate
                          //   • none selected  → checkbox unchecked
                          const groupKeys = group.keys.map(([k]) => k);
                          const checkedInGroup = groupKeys.filter(k => pickedSettingKeys.has(k));
                          const allChecked  = checkedInGroup.length === groupKeys.length;
                          const someChecked = checkedInGroup.length > 0 && !allChecked;

                          const toggleGroup = () => {
                            setPickedSettingKeys(prev => {
                              const next = new Set(prev);
                              if (allChecked) {
                                groupKeys.forEach(k => next.delete(k));
                              } else {
                                groupKeys.forEach(k => next.add(k));
                              }
                              return next;
                            });
                          };
                          const toggleKey = (k) => {
                            setPickedSettingKeys(prev => {
                              const next = new Set(prev);
                              if (next.has(k)) next.delete(k);
                              else next.add(k);
                              return next;
                            });
                          };

                          return (
                            <div key={group.id} className="mb-3">
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <label className="text-xs font-bold flex items-center gap-2 cursor-pointer"
                                  style={{
                                    color: 'var(--text-2)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                  }}>
                                  <input
                                    type="checkbox"
                                    checked={allChecked}
                                    ref={el => { if (el) el.indeterminate = someChecked; }}
                                    onChange={toggleGroup}
                                    style={{ cursor: 'pointer' }}
                                  />
                                  {group.emoji} {group.label} ({checkedInGroup.length}/{groupKeys.length})
                                </label>
                              </div>
                              <div className="space-y-0.5 ml-5">
                                {group.keys.map(([key, displayLabel]) => {
                                  const checked = pickedSettingKeys.has(key);
                                  return (
                                    <label
                                      key={key}
                                      className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm"
                                      style={{
                                        background: checked ? 'rgba(0,212,255,0.06)' : 'transparent',
                                        color: checked ? 'var(--text)' : 'var(--text-2)',
                                      }}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleKey(key)}
                                        style={{ cursor: 'pointer' }}
                                      />
                                      <span className="flex-1 truncate">{displayLabel}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {pickedSettingKeys.size === 0 && (
                        <p className="text-xs mt-2" style={{ color: 'var(--puck)' }}>
                          ⚠️ With nothing selected, the new season starts with empty settings — equivalent to "Start fresh with defaults."
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Apr 30 2026 (App_Change_List #10) — make-active
                checkbox. When checked AND there's a previously-
                active season, an inline note warns about chat/stats
                scope changes. */}
            <div>
              <label className="flex items-start gap-3 p-3 rounded-lg cursor-pointer"
                style={{
                  background: newMakeActive ? 'var(--ice-a12)' : 'var(--surface-2)',
                  border: `1.5px solid ${newMakeActive ? 'var(--ice-a30)' : 'var(--border)'}`,
                }}>
                <input
                  type="checkbox"
                  checked={newMakeActive}
                  onChange={e => setNewMakeActive(e.target.checked)}
                  style={{ marginTop: 3, flexShrink: 0 }}
                />
                <span className="flex-1">
                  <p className="text-sm font-semibold"
                    style={{ color: newMakeActive ? 'var(--ice)' : 'var(--text)' }}>
                    Make this the active season
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                    {newMakeActive && seasons.some(s => s.isActive)
                      ? (newBringSettings === 'none'
                          ? '⚠️ Settings will reset to defaults. Chat and stats also reset. Previous data is preserved on the deactivated season.'
                          : newBringSettings === 'fromSeason'
                            ? 'Settings will be copied from the picked season. Chat and stats reset for the new season. Previous data is preserved.'
                            : 'Settings stay the same as today. Chat and stats reset for the new season. Previous data is preserved.')
                      : 'When active, chat, schedule, stats, and settings all scope to this season.'}
                  </p>
                </span>
              </label>
            </div>
            {/* Apr 2026 (Tom_Notes D2/D3) — max-players and allow-
                overflow have been promoted to team-level settings
                (Settings → Game Settings). Seasons no longer carry
                their own copies; they're just a way to group games
                inside a single team's roster. The previous fields
                here have been removed. Existing per-season values
                in the DB are preserved but are read-only — when a
                season has its OWN max set, we still respect it for
                back-compat (see /seasons GET in routes/seasons.js)
                but we don't expose UI to edit it from here. */}
            <div className="flex gap-2">
              <button onClick={() => {
                  setShowCreate(false);
                  setNewName(''); setNewDesc('');
                  setNewBringOver('none');
                  setNewBringSettings('team');
                  setNewSettingsSourceId('');
                  setNewMakeActive(true);
                  // v2.5.77 reset
                  setPlayerSearchQuery('');
                  setSettingsCustomize(false);
                  setPickedSettingKeys(new Set());
                }}
                className="btn-ghost flex-1 justify-center py-2">Cancel</button>
              <button onClick={createSeason} disabled={creating || !newName.trim()}
                className="btn-ice flex-1 justify-center py-2">
                {creating ? <div className="spinner" style={{ width: 14, height: 14 }} /> : 'Create Season'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Season list */}
      {seasons.length === 0 ? (
        <div className="card py-12 text-center">
          <Calendar size={40} style={{ color: 'var(--text-2)', margin: '0 auto 12px' }} />
          <p className="font-semibold text-secondary mb-1">No seasons yet</p>
          <p className="text-xs text-secondary mb-4">
            Create a season to group games and manage a specific player list — like "Summer 2025 Sunday Hockey".
          </p>
          {isCoach && (
            <button onClick={() => setShowCreate(true)} className="btn-ice mx-auto">
              <Plus size={14} /> Create First Season
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {seasons.map(season => (
            <div key={season.id} className="card"
              style={{
                // Apr 2026 — visually de-emphasize archived seasons.
                // Half-opacity body + subtle gray border so they're
                // easy to spot at a glance without disappearing
                // entirely (coaches still need to see them to
                // reactivate or browse historical data).
                opacity: season.isActive === false ? 0.65 : 1,
                background: season.isActive === false ? 'var(--surface-2)' : undefined,
              }}>
              {editingId === season.id ? (
                /* Inline edit */
                <div className="space-y-2">
                  <input autoFocus type="text" className="input-field"
                    value={editName} maxLength={120}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(season.id); if (e.key === 'Escape') setEditingId(null); }}
                  />
                  <input type="text" className="input-field text-sm"
                    value={editDesc} maxLength={200}
                    placeholder="Description (optional)"
                    onChange={e => setEditDesc(e.target.value)}
                  />
                  {/* Apr 2026 (Tom_Notes D2/D3) — max-players and
                      allow-overflow moved to team-level settings.
                      Edit form no longer surfaces them. The DB
                      values are preserved (and respected by the
                      backend's roster cap logic when set), but
                      coaches manage these via Settings → Game
                      Settings instead. */}
                  <div className="flex gap-2">
                    <button onClick={() => setEditingId(null)} className="btn-ghost flex-1 justify-center py-1.5 text-sm">Cancel</button>
                    <button onClick={() => saveEdit(season.id)} disabled={editSaving}
                      className="btn-ice flex-1 justify-center py-1.5 text-sm">
                      {editSaving ? <div className="spinner" style={{ width: 13, height: 13 }} /> : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Mobile: flex-wrap added so the action-button cluster (Work
                   in this / Calendar / Edit / Activate / Delete) drops to its
                   own row instead of overflowing horizontally at ~390px —
                   mobile .btn-ghost padding balloons to min-height:46px which
                   made this row wider than the card on narrow phones. No-op
                   on desktop since there's always room on one line there. */
                <div className="flex items-start gap-4 flex-wrap">
                  {/* Apr 2026 (Tom_Notes D5) — clicking a season card
                      no longer opens the per-season roster screen.
                      Players belong to the team; seasons are now
                      just a way to group games. So tapping the card
                      goes directly to the schedule filtered by this
                      season, which is what coaches actually want
                      99% of the time. The roster-management UI on
                      the season detail screen is unreachable from
                      here but the underlying endpoints (import,
                      copy, list season members) remain — they're
                      called by the auto-import-on-create path (D4)
                      and any external callers. */}
                  <Link
                    to={`/team/${teamId}/games?seasonId=${season.id}`}
                    className="flex-1 min-w-0"
                    style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-bold truncate">{season.name}</p>
                      {currentSeasonId === season.id && (
                        <span className="text-xs font-bold flex items-center gap-1 px-2 py-0.5 rounded-md flex-shrink-0"
                          style={{ background: 'var(--ice-a12, rgba(0,212,255,0.12))', color: 'var(--ice)', border: '1px solid var(--ice-a30, rgba(0,212,255,0.30))', letterSpacing: '0.04em', textTransform: 'uppercase' }}
                          title="This is the season every page is currently showing.">
                          <Check size={10} /> Current
                        </span>
                      )}
                      {season.isActive === false && (
                        <span className="text-xs font-bold flex items-center gap-1 px-2 py-0.5 rounded-md flex-shrink-0"
                          style={{
                            background: 'rgba(255,149,0,0.10)',
                            color: 'var(--puck)',
                            border: '1px solid rgba(255,149,0,0.25)',
                            letterSpacing: '0.04em', textTransform: 'uppercase',
                          }}
                          title="Hidden from invites and pickers. Players and games are still attached.">
                          <Archive size={10} /> Archived
                        </span>
                      )}
                    </div>
                    {season.description && (
                      <p className="text-xs text-secondary truncate mb-1">{season.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-secondary">
                      <span className="flex items-center gap-1">
                        <Users size={11} /> {season.memberCount} players
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={11} /> {season.gameCount} games
                      </span>
                      {/* Apr 2026 (Tom_Notes D2/D3) — per-season max
                          badge intentionally hidden. The cap is now
                          read from team settings; surfacing a stale
                          per-season value would mislead the coach. */}
                    </div>
                  </Link>

                  {/* flex-wrap: the button cluster itself can also wrap to a
                      second line on very narrow phones (see note above). */}
                  <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                    {currentSeasonId !== season.id && (
                      <button onClick={() => goToSeason(season)}
                        className="btn-ghost text-xs px-2.5 py-2 flex items-center gap-1.5"
                        style={{ color: 'var(--ice)', borderColor: 'var(--ice-a30)', background: 'var(--ice-a8)' }}
                        title="Work in this season — every page will show this season's players, schedule, stats and chat.">
                        <Check size={13} /> Work in this
                      </button>
                    )}
                    <Link to={`/team/${teamId}/games?seasonId=${season.id}`}
                      className="btn-ghost p-2" title="View schedule">
                      <Calendar size={15} />
                    </Link>
                    {isCoach && (
                      <>
                        <button onClick={() => { setEditingId(season.id); setEditName(season.name); setEditDesc(season.description || ''); }}
                          className="btn-ghost p-2" title="Edit">
                          <Edit2 size={15} />
                        </button>
                        {/* Apr 30 2026 (App_Change_List #10) — single-
                            active model. The "Archive" button is gone
                            (deactivating without activating another
                            would leave the team in an undefined
                            scoping state for chat/stats). The button
                            now only ACTIVATES — for the currently-
                            active season it's hidden entirely (the
                            "Active" badge above is the indicator).
                            For inactive seasons, tapping this
                            atomically deactivates the previously-
                            active season AND activates this one. */}
                        {!season.isActive && (
                          <button onClick={() => activateSeason(season)}
                            disabled={togglingId === season.id}
                            className="btn-ghost p-2"
                            title="Make this the active season — chat, stats, and schedule will scope to it"
                            style={{ color: 'var(--ice)' }}>
                            {togglingId === season.id
                              ? <div className="spinner" style={{ width: 14, height: 14 }} />
                              : <RotateCcw size={15} />}
                          </button>
                        )}
                        <button onClick={() => deleteSeason(season)}
                          disabled={deleteWorking}
                          className="btn-ghost text-xs px-2 py-1.5 flex-shrink-0 flex items-center gap-1"
                          style={{ color: deletingId === season.id ? '#fff' : 'var(--penalty)',
                                   background: deletingId === season.id ? 'var(--penalty)' : undefined }}
                          title={deletingId === season.id ? 'Tap again to confirm delete' : 'Delete'}>
                          {deleteWorking && deletingId === season.id
                            ? <div className="spinner" style={{ width: 12, height: 12 }} />
                            : <><Trash2 size={13} />{deletingId === season.id ? ' Confirm?' : ''}</>}
                        </button>
                        {deletingId === season.id && (
                          <button onClick={() => setDeletingId(null)}
                            className="btn-ghost text-xs px-2 py-1.5 flex-shrink-0"
                            title="Cancel delete">
                            <X size={13} />
                          </button>
                        )}
                      </>
                    )}
                    {/* Apr 2026 (Tom_Notes D5) — chevron-right removed.
                        It used to open the per-season roster manager;
                        with players belonging to the team, that screen
                        is no longer the primary action. The card body
                        is now a Link to the schedule for this season. */}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
